import { useEffect, useRef, useState } from 'react';

type MermaidDiagramProps = {
  chart: string;
};

let mermaidSequence = 0;

export function MermaidDiagram({ chart }: MermaidDiagramProps): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const renderDiagram = async () => {
      const container = containerRef.current;
      if (!container) return;

      try {
        const mermaid = (await import('mermaid')).default;
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: 'loose',
          theme: 'base',
          themeVariables: {
            background: '#0f1117',
            primaryColor: '#c8e6c9',
            primaryTextColor: '#1f2933',
            primaryBorderColor: '#388e3c',
            lineColor: '#9fb89f',
            secondaryColor: '#f1f8e9',
            tertiaryColor: '#f9fbe7',
          },
        });

        const id = `mermaid-diagram-${++mermaidSequence}`;
        const { svg } = await mermaid.render(id, chart);

        if (cancelled) return;

        container.innerHTML = svg;
        setError(null);
      } catch (renderError) {
        if (cancelled) return;

        const message = renderError instanceof Error ? renderError.message : 'Unknown Mermaid render error';
        setError(message);
      }
    };

    void renderDiagram();

    return () => {
      cancelled = true;
    };
  }, [chart]);

  if (error) {
    return (
      <div className="mermaid-block mermaid-block--error">
        <p className="mermaid-block__error">Mermaid diagram failed to render.</p>
        <pre className="prose__pre">
          <code className="prose__code">{chart}</code>
        </pre>
      </div>
    );
  }

  return <div ref={containerRef} className="mermaid-block" aria-label="Mermaid diagram" />;
}
